import {
    CreateElectronicContractModel,
    ElectronicContractFieldDefinition,
    ElectronicContractPreview,
    ElectronicContractTemplateDefinition,
} from '@/core/models/electronic-contract.model';
import { ApiResponse } from '@/core/models/response.model';

export type RequestMarkerState = 'draft' | 'submitting' | 'uncertain' | 'failed';
export interface RequestMarker { requestKey: string; state: RequestMarkerState; }

const organizationInputs = ['TenToChuc', 'MaSoThue', 'DiaChiTruSo', 'TenNguoiDaiDien', 'ChucVu', 'CanCuDaiDien', 'EmailNguoiKy', 'SoDienThoaiNguoiKy'];
const isImmutable = (name: string) => name === 'MaKhachHang' || /^[HK](?:[1-9]|1[0-3])$/.test(name);

/** Only catalog overrides and explicit logical inputs may enter the request. */
export function getCreationFields(definition: ElectronicContractTemplateDefinition): ElectronicContractFieldDefinition[] {
    const fields = definition.fields.filter(field => field.allowOverride && !isImmutable(field.name)).map(field => ({ ...field }));
    const inputs = [...definition.requiredInputs, ...(definition.customerType === 'DN' ? organizationInputs : [])];
    inputs.forEach(name => {
        if (isImmutable(name)) return;
        const existing = fields.find(field => field.name === name);
        if (existing) {
            if (definition.requiredInputs.includes(name)) existing.required = true;
        } else {
            fields.push({ name, required: definition.requiredInputs.includes(name), source: 'Thông tin nhân viên xác nhận', format: 'text', allowOverride: true });
        }
    });
    return fields;
}

export function buildCreationPayload(waterUserCode: string, templateId: string, requestKey: string,
    definition: ElectronicContractTemplateDefinition, values: Record<string, string>): CreateElectronicContractModel {
    const fieldValues: Record<string, string> = {};
    getCreationFields(definition).forEach(field => {
        const value = values[field.name];
        if (field.format === 'checkbox') fieldValues[field.name] = value === '☒' ? '☒' : '☐';
        else if (typeof value === 'string' && value !== '') fieldValues[field.name] = value;
    });
    return { waterUserCode, templateId, documentType: definition.documentType, requestKey, fieldValues };
}

export function canIssuePreview(response: ApiResponse<ElectronicContractPreview> | undefined): boolean {
    return response?.code === '00' && response.result?.canCreateFromTemplate === true
        && Array.isArray(response.result.missingRequiredFields) && response.result.missingRequiredFields.length === 0;
}

export function getDraftStorageKey(api: string, factoryId: string, userId: string): string {
    return `electronic-contract-request:${[api, factoryId, userId].map(encodeURIComponent).join(':')}`;
}

export function readRequestMarker(storageKey: string): RequestMarker | null {
    try {
        const raw = localStorage.getItem(storageKey);
        if (!raw) return null;
        const value = JSON.parse(raw);
        if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value.requestKey)
            || !['draft', 'submitting', 'uncertain', 'failed'].includes(value.state)) return null;
        return { requestKey: value.requestKey, state: value.state };
    } catch { return null; }
}

export function writeRequestMarker(storageKey: string, marker: RequestMarker): boolean {
    try {
        const serialized = JSON.stringify({ requestKey: marker.requestKey, state: marker.state });
        localStorage.setItem(storageKey, serialized);
        return localStorage.getItem(storageKey) === serialized;
    } catch { return false; }
}

export function clearRequestMarker(storageKey: string): void {
    try { localStorage.removeItem(storageKey); } catch { /* A successful request is still visible in this session. */ }
}
